import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from "react-leaflet";
import L from "leaflet";
import { X, Target, MapPin } from "@phosphor-icons/react";
import { fetchGps, reverseGeocode } from "../../lib/gps";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

function Click({ onPick }) {
  useMapEvents({ click(e) { onPick(e.latlng.lat, e.latlng.lng); } });
  return null;
}
function Recenter({ lat, lng }) {
  const map = useMap();
  useEffect(() => { if (lat && lng) map.setView([lat, lng], Math.max(map.getZoom(), 13)); }, [lat, lng, map]);
  return null;
}

const VEHICLE_TYPES = [
  { value: "2_wheeler", label: "2 Wheeler" },
  { value: "3_wheeler", label: "3 Wheeler" },
  { value: "4_wheeler", label: "4 Wheeler" },
  { value: "6_wheeler_truck", label: "6 Wheeler Truck" },
];

const useLocLevel = (type, parentId, enabled) => {
  const [state, setState] = useState({ items: [], loading: false, error: "" });
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (!enabled) { setState({ items: [], loading: false, error: "" }); return; }
    let live = true;
    setState(s => ({ ...s, loading: true, error: "" }));
    const params = parentId ? { type, parent_id: parentId } : { type };
    const get = (n) => api.get("/locations", { params }).catch(e => (n > 0 ? get(n - 1) : Promise.reject(e)));
    get(1)
      .then(r => live && setState({ items: (r.data || []).sort((a, b) => String(a.name).localeCompare(String(b.name))), loading: false, error: "" }))
      .catch(e => live && setState({ items: [], loading: false, error: formatApiError(e) }));
    return () => { live = false; };
  }, [type, parentId, enabled, tick]);
  return { ...state, retry: () => setTick(t => t + 1) };
};

export default function CustomerForm({ onClose, onSaved, initial }) {
  const [packages, setPackages] = useState([]);
  // Iter 78 — D-BMW waste-type options for the "Primary waste" chips.
  const [wasteTypeOptions, setWasteTypeOptions] = useState([]);
  const [form, setForm] = useState(initial ? {
    ...initial,
    // Coerce absent legacy fields to sensible editable defaults.
    primary_waste_types: initial.primary_waste_types || [],
    auto_schedule_frequency: initial.auto_schedule_frequency || "on_demand",
  } : {
    name: "", phone: "", alternate_phone: "", email: "",
    latitude: 10.0, longitude: 76.3,
    address: "", landmark: "", house_no: "", house_name: "",
    state_id: "", district_id: "", lsgd_id: "", ward_id: "",
    haritha_mithram_id: "",
    property_ownership: "owner",
    registration_cert_required: null,
    vehicle_access: true, vehicle_type: "",
    customer_type: "residential",
    // Iter 157 — staff-only priority tier badge (regular/priority/vip/vvip)
    priority_tier: "regular",
    // Iter 78 — auto_schedule_frequency replaces the legacy
    // service_frequency in the admin edit form. Default: on_demand.
    auto_schedule_frequency: "on_demand",
    primary_waste_types: [],
    preferred_service_category: "", package_id: "", service_status: "active",
  });

  useEffect(() => {
    api.get("/packages").then((r) => setPackages(r.data)).catch(() => {});
    // Iter 78 — D-BMW waste catalogue (auth OK since admin is logged in).
    // Falls back gracefully if the tenant has no D-BMW config yet.
    api.get("/public/waste-types?category=D-BMW")
      .then((r) => setWasteTypeOptions(r.data || []))
      .catch(() => {});
  }, []);

  // Auto-fetch GPS + reverse-geocode on mount (only for NEW customers; do not
  // clobber existing lat/lng when editing).
  useEffect(() => {
    if (initial?.id) return;
    let cancelled = false;
    (async () => {
      try {
        const pos = await fetchGps({ timeoutMs: 8000 });
        if (cancelled) return;
        setForm(f => ({ ...f, latitude: pos.latitude, longitude: pos.longitude }));
        try {
          const g = await reverseGeocode(pos.latitude, pos.longitude);
          if (cancelled) return;
          setForm(f => ({
            ...f,
            address: f.address || [g.road, g.neighbourhood].filter(Boolean).join(", "),
            landmark: f.landmark || g.neighbourhood || "",
            house_no: f.house_no || g.house_number || "",
          }));
        } catch { /* nominatim optional */ }
      } catch { /* user may deny — non-blocking */ }
    })();
    return () => { cancelled = true; };
  }, [initial?.id]);

  // Iter 260 — cascade loads one level at a time (small, retryable requests).
  const states = useLocLevel("state", "", true);
  const districts = useLocLevel("district", form.state_id, !!form.state_id);
  const lsgds = useLocLevel("lsgd", form.district_id, !!form.district_id);
  const wards = useLocLevel("ward", form.lsgd_id, !!form.lsgd_id);
  const locError = [states, districts, lsgds, wards].find(l => l.error);
  const retryLocations = () => [states, districts, lsgds, wards].forEach(l => l.error && l.retry());

  // Auto-resolve Health Zone from ward → master mapping
  const [zoneInfo, setZoneInfo] = useState({ status: "idle", name: null, error: null });
  useEffect(() => {
    if (!form.ward_id) { setZoneInfo({ status: "idle", name: null, error: null }); return; }
    setZoneInfo({ status: "loading", name: null, error: null });
    api.get(`/public/ward-zone/${form.ward_id}`)
      .then(r => setZoneInfo({ status: "ok", name: r.data.zone_name, error: null, id: r.data.zone_id }))
      .catch(e => setZoneInfo({ status: "missing", name: null, error: e?.response?.data?.detail || "Not mapped" }));
  }, [form.ward_id]);

  const [gpsBusy, setGpsBusy] = useState(false);
  const useGps = async () => {
    setGpsBusy(true);
    try {
      const pos = await fetchGps();
      setForm(f => ({ ...f, latitude: pos.latitude, longitude: pos.longitude }));
      try {
        const g = await reverseGeocode(pos.latitude, pos.longitude);
        setForm(f => ({
          ...f,
          address: [g.road, g.neighbourhood].filter(Boolean).join(", ") || f.address,
          landmark: g.neighbourhood || f.landmark,
          house_no: g.house_number || f.house_no,
        }));
        toast.success(`Location set · ${g.display_name?.slice(0, 60) || "address filled"}`);
      } catch {
        toast.success(`Location set${pos.accuracy ? ` · ±${Math.round(pos.accuracy)} m` : ""}`);
      }
    } catch (e) {
      toast.error(e.message || "Could not fetch GPS");
    } finally {
      setGpsBusy(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.lsgd_id) { toast.error("LSGD is required"); return; }
    if (!form.ward_id) { toast.error("Ward is required"); return; }
    if (!(form.haritha_mithram_id || "").trim()) {
      toast.error("Haritha Mithram ID is now mandatory for every customer");
      return;
    }
    if (zoneInfo.status === "missing") {
      toast.error("Selected ward is not mapped to any Health Zone — please map it first via Master Control → Operational Zones");
      return;
    }
    try {
      const payload = {
        name: form.name, phone: form.phone,
        alternate_phone: form.alternate_phone || null,
        email: form.email || null,
        latitude: Number(form.latitude), longitude: Number(form.longitude),
        address: form.address, landmark: form.landmark,
        house_no: form.house_no, house_name: form.house_name,
        state_id: form.state_id || null,
        district_id: form.district_id || null,
        lsgd_id: form.lsgd_id || null,
        ward_id: form.ward_id || null,
        haritha_mithram_id: form.haritha_mithram_id.trim(),
        property_ownership: form.property_ownership || "owner",
        registration_cert_required: (
          form.registration_cert_required === true ? true
          : form.registration_cert_required === false ? false
          : null
        ),
        vehicle_access: !!form.vehicle_access,
        vehicle_type: form.vehicle_type || null,
        customer_type: form.customer_type,
        priority_tier: form.priority_tier || "regular",
        auto_schedule_frequency: form.auto_schedule_frequency || "on_demand",
        primary_waste_types: form.primary_waste_types || [],
        preferred_service_category: form.preferred_service_category || null,
        package_id: form.package_id || null,
        service_status: form.service_status || "active",
        waste_types: form.waste_types || [],
      };
      if (initial?.id) {
        await api.put(`/customers/${initial.id}`, payload);
        toast.success("Customer updated");
      } else {
        await api.post("/customers", payload);
        toast.success("Customer created");
      }
      onSaved?.();
      onClose?.();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white border border-[#E5E2D9] rounded-md w-full max-w-3xl max-h-[92vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-6 border-b border-[#E5E2D9] sticky top-0 bg-white z-10">
          <div>
            <div className="text-[11px] uppercase tracking-[0.2em] text-[#8A8780]">Customer Mgmt</div>
            <div className="font-heading text-lg">{initial?.id ? "Edit customer" : "New customer"}</div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-[#F7F6F2] rounded"><X size={16} /></button>
        </div>

        <form onSubmit={submit} className="p-6 space-y-5" data-testid="customer-form">
          <div className="grid grid-cols-2 gap-4">
            <Field label="Full name *" testid="cf-name" value={form.name} onChange={(v) => setForm(f => ({ ...f, name: v }))} required />
            <Field label="Phone *" testid="cf-phone" value={form.phone} onChange={(v) => setForm(f => ({ ...f, phone: v }))} required />
            <Field label="Alternate phone" value={form.alternate_phone} onChange={(v) => setForm(f => ({ ...f, alternate_phone: v }))} />
            <Field label="Email" type="email" value={form.email} onChange={(v) => setForm(f => ({ ...f, email: v }))} />
          </div>

          {/* Address block */}
          <div className="pt-3 border-t border-[#E5E2D9]">
            <div className="text-[11px] uppercase tracking-[0.2em] text-[#8A8780] mb-3">Address</div>
            <div className="grid grid-cols-2 gap-4">
              <Field label="House no" value={form.house_no} onChange={(v) => setForm(f => ({ ...f, house_no: v }))} />
              <Field label="House name" value={form.house_name} onChange={(v) => setForm(f => ({ ...f, house_name: v }))} />
              <Field label="Landmark" value={form.landmark} onChange={(v) => setForm(f => ({ ...f, landmark: v }))} />
              <Field label="Area / Street" value={form.address} onChange={(v) => setForm(f => ({ ...f, address: v }))} />
            </div>
          </div>

          {/* Location hierarchy cascade */}
          <div className="pt-3 border-t border-[#E5E2D9]">
            <div className="text-[11px] uppercase tracking-[0.2em] text-[#8A8780] mb-3">Location hierarchy</div>
            <div className="grid grid-cols-2 gap-4">
              <SelectField label="State *" testid="cf-state" value={form.state_id}
                onChange={(v) => setForm(f => ({ ...f, state_id: v, district_id: "", lsgd_id: "", ward_id: "" }))}
                options={[{ value: "", label: states.loading ? "Loading states…" : "— select state —" }, ...states.items.map(s => ({ value: s.id, label: s.name }))]} />
              <SelectField label="District *" testid="cf-district" value={form.district_id}
                disabled={!form.state_id}
                onChange={(v) => setForm(f => ({ ...f, district_id: v, lsgd_id: "", ward_id: "" }))}
                options={[{ value: "", label: form.state_id ? "— select —" : "Select state first" }, ...districts.items.map(s => ({ value: s.id, label: s.name }))]} />
              <SelectField label="LSGD *" testid="cf-lsgd" value={form.lsgd_id}
                disabled={!form.district_id}
                onChange={(v) => setForm(f => ({ ...f, lsgd_id: v, ward_id: "" }))}
                options={[{ value: "", label: form.district_id ? "— select —" : "Select district first" }, ...lsgds.items.map(s => ({ value: s.id, label: `${s.name}${s.lsgd_type ? ` (${s.lsgd_type.replace("_", " ")})` : ""}` }))]} />
              <WardSearchField
                label="Ward *"
                testid="cf-ward"
                value={form.ward_id}
                wards={wards.items}
                lsgdSelected={!!form.lsgd_id}
                onChange={(v) => setForm(f => ({ ...f, ward_id: v }))}
              />
            </div>

            {locError && (
              <div className="mt-3 p-2 rounded-md border border-[#E8C4B6] bg-[#FBEDE8] text-xs text-[#7A2E18] flex items-center gap-2" data-testid="cf-location-error">
                <span className="flex-1">Couldn't load locations: {locError.error}</span>
                <button type="button" onClick={retryLocations} className="h-7 px-3 border border-[#7A2E18] rounded font-bold" data-testid="cf-location-retry">Retry</button>
              </div>
            )}

            {/* Auto-derived Health Zone (read-only) */}
            <div className="mt-3" data-testid="cf-zone-block">
              <label className="text-[10px] uppercase tracking-[0.18em] text-[#8A8780] block mb-1.5">Health Zone (auto)</label>
              <div className={`h-10 px-3 inline-flex items-center gap-2 rounded-md border w-full text-sm ${
                zoneInfo.status === "ok" ? "bg-[#EFF3EE] border-[#2D5A27] text-[#2D5A27]" :
                zoneInfo.status === "missing" ? "bg-[#F4C7B9] border-[#C25D38] text-[#7A2E18]" :
                "bg-[#F7F6F2] border-[#E5E2D9] text-[#8A8780]"
              }`} data-testid="cf-zone-display">
                {!form.ward_id && <span>Select a ward first</span>}
                {form.ward_id && zoneInfo.status === "loading" && <span>Resolving…</span>}
                {zoneInfo.status === "ok" && <span><strong>{zoneInfo.name}</strong> — auto-assigned</span>}
                {zoneInfo.status === "missing" && <span>⚠ {zoneInfo.error} — admin must map this ward</span>}
              </div>
            </div>
          </div>

          {/* Haritha Mithram + Property + Reg cert */}
          <div className="pt-3 border-t border-[#E5E2D9] space-y-4">
            <Field label="Haritha Mithram ID *" testid="cf-haritha"
                   value={form.haritha_mithram_id}
                   onChange={(v) => setForm(f => ({ ...f, haritha_mithram_id: v }))}
                   required />

            <div>
              <label className="text-[11px] uppercase tracking-[0.2em] text-[#5C5A55] block mb-2">Property ownership *</label>
              <div className="flex gap-2">
                {[
                  { v: "owner",  l: "Owner" },
                  { v: "rented", l: "Rented" },
                ].map((x) => (
                  <button type="button" key={x.v}
                          onClick={() => setForm(f => ({ ...f, property_ownership: x.v }))}
                          data-testid={`cf-prop-${x.v}`}
                          className={`flex-1 h-10 rounded-md text-sm border transition-colors ${form.property_ownership === x.v ? "bg-[#D4E0D1] border-[#2D5A27] text-[#1A3317]" : "border-[#E5E2D9]"}`}>
                    {x.l}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="text-[11px] uppercase tracking-[0.2em] text-[#5C5A55] block mb-2">
                Registration certificate required for licence? *
              </label>
              <div className="flex gap-2">
                {[
                  { v: true,  l: "Yes" },
                  { v: false, l: "No" },
                ].map((x) => (
                  <button type="button" key={String(x.v)}
                          onClick={() => setForm(f => ({ ...f, registration_cert_required: x.v }))}
                          data-testid={`cf-regcert-${x.v ? "yes" : "no"}`}
                          className={`flex-1 h-10 rounded-md text-sm border transition-colors ${form.registration_cert_required === x.v ? "bg-[#D4E0D1] border-[#2D5A27] text-[#1A3317]" : "border-[#E5E2D9]"}`}>
                    {x.l}
                  </button>
                ))}
              </div>
              {form.registration_cert_required === null && (
                <div className="text-[10px] mt-1.5 text-[#7A5A11]">Please answer — this drives the customer&apos;s licence compliance flow.</div>
              )}
            </div>
          </div>

          {/* Vehicle access */}
          <div className="pt-3 border-t border-[#E5E2D9]">
            <div className="text-[11px] uppercase tracking-[0.2em] text-[#8A8780] mb-3">Vehicle access to premises</div>
            <div className="flex gap-2 mb-3">
              {[{ v: true, l: "Yes, vehicle can reach" }, { v: false, l: "No vehicle access" }].map((x) => (
                <button type="button" key={String(x.v)}
                        onClick={() => setForm((d) => ({ ...d, vehicle_access: x.v, vehicle_type: x.v ? d.vehicle_type : "" }))}
                        data-testid={`cf-vacc-${x.v ? "yes" : "no"}`}
                        className={`flex-1 h-10 rounded-md text-sm border transition-colors ${form.vehicle_access === x.v ? "bg-[#D4E0D1] border-[#2D5A27] text-[#1A3317]" : "border-[#E5E2D9]"}`}>
                  {x.l}
                </button>
              ))}
            </div>
            {form.vehicle_access && (
              <SelectField label="Vehicle type that can reach" testid="cf-vtype"
                           value={form.vehicle_type}
                           onChange={(v) => setForm(f => ({ ...f, vehicle_type: v }))}
                           options={[{ value: "", label: "— select —" }, ...VEHICLE_TYPES]} />
            )}
          </div>

          {/* Map GPS */}
          <div className="pt-3 border-t border-[#E5E2D9]">
            <div className="text-[11px] uppercase tracking-[0.2em] text-[#8A8780] mb-2 flex items-center gap-2">
              <MapPin size={12} /> GPS Location (click map to pick)
            </div>
            <div className="h-64 border border-[#E5E2D9] rounded-md overflow-hidden">
              <MapContainer center={[form.latitude, form.longitude]} zoom={12} style={{ height: "100%", width: "100%" }}>
                <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution='&copy; OSM' />
                <Click onPick={(lat, lng) => setForm(f => ({ ...f, latitude: lat, longitude: lng }))} />
                <Recenter lat={form.latitude} lng={form.longitude} />
                <Marker position={[form.latitude, form.longitude]} />
              </MapContainer>
            </div>
            <div className="flex items-center gap-2 mt-2">
              <input type="number" step="any" required value={form.latitude}
                     onChange={(e) => setForm(f => ({ ...f, latitude: e.target.value }))}
                     className="h-9 px-2 border border-[#E5E2D9] rounded-md font-mono text-sm flex-1" />
              <input type="number" step="any" required value={form.longitude}
                     onChange={(e) => setForm(f => ({ ...f, longitude: e.target.value }))}
                     className="h-9 px-2 border border-[#E5E2D9] rounded-md font-mono text-sm flex-1" />
              <button type="button" onClick={useGps} disabled={gpsBusy}
                      data-testid="cf-gps-btn"
                      className="h-9 px-3 border border-[#E5E2D9] rounded-md text-xs inline-flex items-center gap-1.5 disabled:opacity-60 hover:bg-[#F7F6F2]">
                <Target size={12} /> {gpsBusy ? "Locating…" : "My location"}
              </button>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 pt-3 border-t border-[#E5E2D9]">
            <SelectField label="Customer type" value={form.customer_type} onChange={(v) => setForm(f => ({ ...f, customer_type: v }))}
                         options={[{ value: "residential", label: "Residential" }, { value: "commercial", label: "Commercial" }]} />
            <SelectField label="Priority tier · staff-only badge" testid="cf-priority-tier"
                         value={form.priority_tier || "regular"}
                         onChange={(v) => setForm(f => ({ ...f, priority_tier: v }))}
                         options={[
                           { value: "regular", label: "Regular" },
                           { value: "priority", label: "Priority" },
                           { value: "vip", label: "VIP" },
                           { value: "vvip", label: "VVIP" },
                         ]} />
            <SelectField label="Pickup schedule" testid="cf-freq" value={form.auto_schedule_frequency || "on_demand"}
                         onChange={(v) => setForm(f => ({ ...f, auto_schedule_frequency: v }))}
                         options={[
                           { value: "on_demand", label: "On-demand only" },
                           { value: "weekly", label: "Every week" },
                           { value: "alt_weekly", label: "Alternate week" },
                           { value: "monthly", label: "Monthly once" },
                         ]} />
            <SelectField label="Preferred service category" testid="cf-pref-cat"
                         value={form.preferred_service_category || ""}
                         onChange={(v) => setForm(f => ({ ...f, preferred_service_category: v }))}
                         options={[
                           { value: "", label: "— not set —" },
                           { value: "drop", label: "Drop (customer drops)" },
                           { value: "home_service", label: "Home collection" },
                           { value: "both", label: "Both" },
                         ]} />
            <SelectField label="Service package" testid="cf-pkg"
                         value={form.package_id || ""}
                         onChange={(v) => setForm(f => ({ ...f, package_id: v }))}
                         options={[
                           { value: "", label: "— no package —" },
                           ...packages.map((p) => ({
                             value: p.id,
                             label: `${p.name} · ₹${Number(p.price).toFixed(0)}${p.gst_percent > 0 ? ` · ${p.gst_percent}% ${p.gst_inclusive ? "incl" : "excl"}` : ""}`,
                           })),
                         ]} />
            <SelectField label="Service status" testid="cf-svc-status"
                         value={form.service_status || "active"}
                         onChange={(v) => setForm(f => ({ ...f, service_status: v }))}
                         options={[
                           { value: "active", label: "Active" },
                           { value: "inactive", label: "Inactive" },
                           { value: "temporarily_unavailable", label: "Temporarily unavailable" },
                           { value: "stopped", label: "Stopped" },
                         ]} />
          </div>

          {/* Iter 78 — Primary waste (D-BMW) multi-select. Full-width
              row so the chips have room to wrap on 3-item D-BMW config. */}
          <div className="pt-3 border-t border-[#E5E2D9]">
            <label className="text-[11px] uppercase tracking-[0.2em] text-[#5C5A55] block mb-2">
              Primary waste (D-BMW)
            </label>
            <div className="flex flex-wrap gap-2" data-testid="cf-primary-waste">
              {wasteTypeOptions.length === 0 && (
                <div className="text-[11px] text-[#8A8780] italic">
                  No D-BMW types configured for this tenant.
                </div>
              )}
              {wasteTypeOptions.map((o) => {
                const on = (form.primary_waste_types || []).includes(o.id);
                return (
                  <button key={o.id} type="button"
                          onClick={() => setForm(f => ({
                            ...f,
                            primary_waste_types: on
                              ? f.primary_waste_types.filter(x => x !== o.id)
                              : [...(f.primary_waste_types || []), o.id],
                          }))}
                          data-testid={`cf-primary-waste-${o.id}`}
                          aria-pressed={on}
                          className={`h-9 px-3 rounded-full border text-xs font-medium transition-colors ${
                            on
                              ? "bg-[#2D5A27] text-white border-[#2D5A27]"
                              : "bg-white text-[#1A1A1A] border-[#E5E2D9] hover:border-[#2D5A27]"
                          }`}>
                    {on ? "✓ " : ""}{o.name}
                  </button>
                );
              })}
            </div>
            <div className="text-[10px] text-[#8A8780] mt-1.5">
              Items this customer regularly disposes of. Ops uses these chips to plan the right bags + vans.
            </div>
          </div>

          <div className="pt-4 flex gap-3 border-t border-[#E5E2D9]">
            <button type="submit" data-testid="cf-save"
                    className="flex-1 h-10 bg-[#2D5A27] hover:bg-[#3A7033] text-white rounded-md text-sm font-medium">
              {initial?.id ? "Save changes" : "Save customer"}
            </button>
            <button type="button" onClick={onClose}
                    className="h-10 px-4 border border-[#E5E2D9] rounded-md text-sm">Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", required, testid, ...rest }) {
  return (
    <div>
      <label className="text-[11px] uppercase tracking-[0.2em] text-[#5C5A55] block mb-2">{label}</label>
      <input {...rest} data-testid={testid} type={type} value={value || ""}
             required={required}
             onChange={(e) => onChange(e.target.value)}
             className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md" />
    </div>
  );
}
function SelectField({ label, value, onChange, options, disabled, testid }) {
  return (
    <div>
      <label className="text-[11px] uppercase tracking-[0.2em] text-[#5C5A55] block mb-2">{label}</label>
      <select data-testid={testid} value={value || ""} onChange={(e) => onChange(e.target.value)}
              disabled={disabled}
              className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md bg-white disabled:bg-[#F7F6F2] disabled:opacity-60">
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );
}


/**
 * Iter 102 — searchable Ward picker. Type-ahead over ward_no + name.
 * Renders a plain input + dropdown of matches once the LSGD is selected.
 */
function WardSearchField({ label, testid, value, wards, lsgdSelected, onChange }) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const selected = wards.find(w => w.id === value);

  // Keep query in sync when the selected ward changes externally
  useEffect(() => {
    if (selected) setQuery(`${selected.ward_no ? `#${selected.ward_no} · ` : ""}${selected.name}`);
    else setQuery("");
  }, [selected]);

  const filtered = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q || (selected && `${selected.ward_no ? `#${selected.ward_no} · ` : ""}${selected.name}`.toLowerCase() === q)) {
      return wards.slice(0, 40);
    }
    return wards.filter(w => {
      const label = `${w.ward_no || ""} ${w.name || ""}`.toLowerCase();
      return label.includes(q);
    }).slice(0, 40);
  }, [query, wards, selected]);

  const pick = (w) => {
    onChange(w.id);
    setQuery(`${w.ward_no ? `#${w.ward_no} · ` : ""}${w.name}`);
    setOpen(false);
  };
  const clear = () => { onChange(""); setQuery(""); };

  return (
    <div className="relative">
      <label className="text-[11px] uppercase tracking-[0.2em] text-[#5C5A55] block mb-2">{label}</label>
      <div className="relative">
        <input data-testid={testid}
                value={query}
                onChange={(e) => { setQuery(e.target.value); setOpen(true); onChange(""); }}
                onFocus={() => lsgdSelected && setOpen(true)}
                onBlur={() => setTimeout(() => setOpen(false), 150)}
                disabled={!lsgdSelected}
                placeholder={lsgdSelected ? "Type ward no or name…" : "Select LSGD first"}
                className="w-full h-10 pl-3 pr-8 border border-[#E5E2D9] rounded-md bg-white text-sm disabled:bg-[#F7F6F2] disabled:opacity-60"/>
        {query && lsgdSelected && (
          <button type="button" onClick={clear}
                  data-testid={`${testid}-clear`}
                  className="absolute right-2 top-1/2 -translate-y-1/2 w-5 h-5 rounded-full text-[#8A8780] hover:bg-[#F7F6F2] flex items-center justify-center">
            <X size={12}/>
          </button>
        )}
      </div>
      {open && lsgdSelected && filtered.length > 0 && (
        <div data-testid={`${testid}-dropdown`}
             className="absolute z-30 mt-1 w-full bg-white border border-[#E5E2D9] rounded-md shadow-xl max-h-56 overflow-y-auto">
          {filtered.map(w => (
            <button type="button" key={w.id}
                    data-testid={`${testid}-opt-${w.id}`}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => pick(w)}
                    className={`w-full text-left px-3 py-2 text-sm hover:bg-[#F0F7EE] border-b border-[#F0EEE6] last:border-0 ${
                      w.id === value ? "bg-[#EFF3EE]" : ""
                    }`}>
              <span className="font-medium text-[#1A1A1A]">
                {w.ward_no ? `#${w.ward_no}` : "—"} · {w.name}
              </span>
            </button>
          ))}
        </div>
      )}
      {open && lsgdSelected && filtered.length === 0 && query && (
        <div className="absolute z-30 mt-1 w-full bg-white border border-[#E5E2D9] rounded-md shadow px-3 py-2 text-xs text-[#8A8780]">
          No wards match "{query}".
        </div>
      )}
    </div>
  );
}
